import React from 'react';
import Badge from '@components/common/Badge';
import Message from '@components/common/Message';
import { LoadingSpinner } from '@components/common/loading';
import type { DivisionVersionHistoryEntry, DivisionVersionStatus } from '@app-types/division';
import type { BadgeVariant } from '@utils/badgeMappers';
import { formatVersionDate, leadsAtLevel, type LeadLevel } from '@utils/divisionVersionUtils';
import styles from './DivisionVersionHistory.module.css';

export interface DivisionVersionHistoryProps {
  /** As the server returns them: newest first. */
  versions: DivisionVersionHistoryEntry[];
  isLoading: boolean;
  /** Shown inside the section only, so the rest of the Edit modal keeps working. */
  error: string | null;
}

interface StatusDisplay {
  label: string;
  variant: BadgeVariant;
}

const STATUS_DISPLAY: Record<DivisionVersionStatus, StatusDisplay> = {
  future: { label: 'Future', variant: 'info' },
  current: { label: 'Current', variant: 'success' },
  ended: { label: 'Ended', variant: 'default' }
};

interface LeadRow {
  level: LeadLevel;
  label: string;
}

const LEAD_ROWS: LeadRow[] = [
  { level: 'l1', label: 'Lead L1' },
  { level: 'l2', label: 'Lead L2' },
  { level: 'l3', label: 'Lead L3' }
];

const DASH = '—';

const leadNames = (entry: DivisionVersionHistoryEntry, level: LeadLevel): string => {
  const leads = leadsAtLevel(entry.leads, level);
  return leads.length > 0 ? leads.map((l) => l.user_name).join(', ') : 'none';
};

interface HistoryEntryProps {
  entry: DivisionVersionHistoryEntry;
}

const HistoryEntry: React.FC<HistoryEntryProps> = ({ entry }) => {
  const { version } = entry;
  const status = STATUS_DISPLAY[entry.status];
  return (
    <li className={styles.entry} data-test-id={`division-version-history-entry-${version}`}>
      <div className={styles.entryHeader}>
        <span className={styles.versionLabel}>Version {version}</span>
        <Badge variant={status.variant} dataTestId={`division-version-history-status-${version}`}>
          {status.label}
        </Badge>
        <span className={styles.dates} data-test-id={`division-version-history-dates-${version}`}>
          {formatVersionDate(entry.effective_date)} – {entry.end_date ? formatVersionDate(entry.end_date) : DASH}
        </span>
      </div>
      <dl className={styles.details}>
        <dt>Name</dt>
        <dd>{entry.name}</dd>
        <dt>Description</dt>
        <dd data-test-id={`division-version-history-description-${version}`}>{entry.description || DASH}</dd>
        {LEAD_ROWS.map(({ level, label }) => (
          <React.Fragment key={level}>
            <dt>{label}</dt>
            <dd data-test-id={`division-version-history-${level}-${version}`}>{leadNames(entry, level)}</dd>
          </React.Fragment>
        ))}
      </dl>
      <p className={styles.changedBy} data-test-id={`division-version-history-changed-by-${version}`}>
        Last changed by: {entry.last_changed_by_name || DASH}
      </p>
    </li>
  );
};

/** Read-only list of every Division Version in the Edit Division modal (#1163). */
const DivisionVersionHistory: React.FC<DivisionVersionHistoryProps> = ({ versions, isLoading, error }) => {
  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={styles.loading} data-test-id="division-version-history-loading">
          <LoadingSpinner size="small" />
          <span>Loading version history...</span>
        </div>
      );
    }
    if (error) {
      return (
        <Message variant="error" data-test-id="division-version-history-error" role="alert">
          <span>{error}</span>
        </Message>
      );
    }
    return (
      <ol className={styles.entries}>
        {versions.map((entry) => (
          <HistoryEntry key={entry.id} entry={entry} />
        ))}
      </ol>
    );
  };

  return (
    <section data-test-id="division-version-history">
      <h4>Version History</h4>
      {renderBody()}
    </section>
  );
};

export default DivisionVersionHistory;
